import { addImageParams, AddImageParamTypes, getAdQuestionUrl, getCatalogUrl, getSellerUrl } from '@/utils/urls';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import { RouteButton } from '../RouteButton/RouteButton';
import { RouteLink, WeakRouteLink } from '../RouteLinks/RouteLinks';
import { safelyEscapeText } from '@/utils/safelyRender';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WeakLink } from '@liveauctioneers/hammer-ui-core/link';
import HoverIntent from '@liveauctioneers/caterwaul-components/lib/HoverIntent/HoverIntent';
import noop from '@/utils/noop';
import styled, { useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import useSSRFriendlyDate from '@/utils/useSSRFriendlyDate';

const imageParams: AddImageParamTypes = {
    height: 104,
    width: 104,
};

type Props = {
    adId?: string;
    adType: string;
    className?: string;
    pageName: string;
    removeBottomMargin?: boolean;
};

const CategoryAd = ({ adId, adType, className, pageName, removeBottomMargin }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();
    const options = {
        ...(adId ? { categoryIds: adId } : null),
        pageName: pageName,
    };
    const {
        adUnitId,
        catalogId,
        catalogTitle,
        destination,
        imageUrl1,
        imageUrl2,
        imageUrl3,
        saleStartTs,
        sellerId,
        sellerName,
    } = useAppSelector((state) => getCatalogAd(state, options));

    const [catalogLoaded, setCatalogLoaded] = useState(false);

    const clientSaleStartTs = useSSRFriendlyDate(saleStartTs);

    const goTo = destination || getCatalogUrl(catalogId, catalogTitle);
    const images = useMemo(() => {
        return [imageUrl1, imageUrl2, imageUrl3].filter((image) => Boolean(image));
    }, [imageUrl1, imageUrl2, imageUrl3]);
    const sellerUrl = getSellerUrl(sellerId, sellerName);

    const hideButton = useIsNarrowerThan(904);

    useEffect(() => {
        if (Boolean(adUnitId)) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: adType,
                })
            );
            trackAmpSeen({ ampProduct: AmpProduct.LeaderboardAd, catalogId, houseId: sellerId });
        }
    }, [dispatch, adUnitId, catalogId, adType, sellerId, trackAmpSeen]);

    const trackClick = useCallback(() => {
        if (Boolean(adUnitId)) {
            dispatch(
                trackAdUnitClick({
                    adUnitId,
                    consignment: false,
                    page: adType,
                })
            );
            trackAmpClicked({ ampProduct: AmpProduct.LeaderboardAd, catalogId, houseId: sellerId });
        }
    }, [dispatch, adUnitId, catalogId, adType, sellerId, trackAmpClicked]);

    const mapImages = useCallback((url: string) => {
        return (
            <StyledImageContainer key={`${url}-catalog-banner-ad-image`}>
                <StyledImage
                    height={String(imageParams.height / 2)}
                    src={addImageParams({ ...imageParams, url })}
                    width={String(imageParams.width / 2)}
                />
            </StyledImageContainer>
        );
    }, []);

    const loadCatalog = useCallback(() => {
        if (!catalogLoaded) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
            setCatalogLoaded(true);
        }
    }, [catalogId, catalogLoaded, dispatch]);

    // do not render on mobile or if no ad is present
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const mobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);
    if (!Boolean(adUnitId) || mobile) {
        return null;
    }

    return (
        <HoverIntent
            interval={50}
            onMouseOut={noop}
            onMouseOver={loadCatalog}
            sensitivity={5}
            timeout={50}
        >
            <StyledContainer
                $removeBottomMargin={removeBottomMargin}
                className={className}
                data-testid="ad-container"
                onClick={trackClick}
            >
                <StyledCategoryAd>
                    <StyledAdLink to={goTo}> </StyledAdLink>
                    <StyledImagesContainer>{images.map(mapImages)}</StyledImagesContainer>
                    <StyledCopyContainer>
                        <StyledAuctionName>{safelyEscapeText(catalogTitle)}</StyledAuctionName>
                        <StyledSubTitleContainer>
                            <StyledAuctionDate color="secondary">{clientSaleStartTs}</StyledAuctionDate>
                            <StyledAuctionNameLink
                                size="xs"
                                to={sellerUrl}
                            >
                                {safelyEscapeText(sellerName)}
                            </StyledAuctionNameLink>
                            <StyledYourAdvertisement color="secondary">
                                <FormattedMessage
                                    id="advertisement"
                                    tagName="span"
                                />{' '}
                                <StyledWeakLink
                                    href={getAdQuestionUrl()}
                                    size="xs"
                                    target="_blank"
                                >
                                    <FormattedMessage id="your_ad_here" />
                                </StyledWeakLink>
                            </StyledYourAdvertisement>
                        </StyledSubTitleContainer>
                    </StyledCopyContainer>
                    {!hideButton && (
                        <StyledButton
                            data-testid="explore-auction-button"
                            size="sm"
                            to={goTo}
                            variant="transparent"
                        >
                            {formatMessage({ id: 'explore_auction' })}
                        </StyledButton>
                    )}
                </StyledCategoryAd>
            </StyledContainer>
        </HoverIntent>
    );
};

export default CategoryAd;

const StyledContainer = styled.div<{ $removeBottomMargin?: boolean }>`
    width: 100%;
    padding: 16px 0;
    margin: 0 auto;

    background-color: ${({ theme }) => theme.colors.beige100};

    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const StyledCategoryAd = styled.div`
    text-decoration: none;

    display: flex;
    align-items: center;

    width: 100%;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    padding: 0 20px;

    position: relative;
`;

const StyledAdLink = styled(RouteLink)`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const StyledImagesContainer = styled.div`
    width: 156px;
    height: 52px;
    flex: 0 0 156px;
    display: flex;
    margin-right: 24px;
`;

const StyledImageContainer = styled.div`
    width: 52px;
    height: 52px;
    margin-right: 1px;

    &:last-child {
        margin-right: 0px;
    }

    background-color: ${({ theme }) => theme.colors.white};
    flex: 0 0 52px;
    overflow: hidden;
`;

const StyledImage = styled.img`
    object-fit: cover;
`;

const StyledCopyContainer = styled.div`
    display: flex;
    flex-direction: column;

    overflow: hidden;
`;

const StyledAuctionName = styled(BodySecondary)`
    max-width: 500px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
`;

const StyledSubTitleContainer = styled.div`
    display: flex;
    flex-direction: row;

    & > * {
        margin-left: 16px;
        &:before {
            content: '|';
            margin-right: 16px;
        }

        &:first-child {
            margin-left: 0;
            &:before {
                content: '';
                margin-right: 0;
            }
        }
    }
`;

const StyledAuctionDate = styled(Caption)`
    white-space: nowrap;
`;

const StyledAuctionNameLink = styled(WeakRouteLink)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 120px;

    z-index: 1;

    pointer-events: all;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const StyledYourAdvertisement = styled(Caption)`
    white-space: nowrap;

    position: relative;
    pointer-events: none;
    z-index: 1;
`;

const StyledWeakLink = styled(WeakLink)`
    z-index: 1;

    pointer-events: all;
`;

const StyledButton = styled(RouteButton)`
    margin-left: auto;
`;
